// IMPORTANT: This component is essentially the same as the horizontal-create.tsx button with a few minor
// changes. We are building this seperate because later in this project we will likely be
// replacing this with the @atlassian/universal-create component.
import { useState, useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { MenuGroup, Section, LinkItem } from '@atlaskit/menu';
import { CreateButton } from '@atlaskit/navigation-system/top-nav-items';
import { Popup } from '@atlaskit/popup';

import { messages } from 'src/components/navigation/src/components/horizontal-nav/horizontal-nav.i18n';
import { MenuItem } from 'src/components/navigation/src/types';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { useTopNavData } from './hooks';

// this is similar to the horizontal-navigation-menu-item but uses only what we
// need for nav 4 and adds logic that we need for using an absolute url as the href
// to bypass the client side linking for our pages still in core
function Nav4LinkItem({
  menuItem,
  onClick,
}: {
  menuItem: MenuItem;
  onClick: () => void;
}) {
  const handleClick = useCallback(() => {
    // publish event when option is selected from Create dropdown
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'menuItem',
      actionSubjectId: 'topNavCreate',
      source: 'navigation',
      attributes: {
        itemType: menuItem.analytics_label,
      },
    });
    // close the dropdown
    onClick?.();
  }, [onClick, menuItem.analytics_label]);

  // once we iframe core pages we can change this to always do a client side link
  const linkHref = useMemo(() => {
    const relativeHref = menuItem.url;
    // check if should use router link
    if (menuItem.is_client_link) {
      return relativeHref;
    } else {
      // absolute url so LinkItem doesn't use the router link
      return `${window.location.origin}${relativeHref}`;
    }
  }, [menuItem.url, menuItem.is_client_link]);

  // LinkItem handles using the correct router link from our AppProvider (routerLinkComponent)
  // if it's a relative URL otherwise it will return an anchor
  return (
    <LinkItem
      href={linkHref}
      target={menuItem.target}
      testId={menuItem?.test_id || undefined}
      onClick={handleClick}
    >
      {menuItem.label}
    </LinkItem>
  );
}

export const TopNavCreateButton = (): JSX.Element | null => {
  const intl = useIntl();
  const { globalBitbucketActions, containerBitbucketActions } = useTopNavData();

  const [isOpen, setIsOpen] = useState(false);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onToggle = useCallback(() => {
    // publish click event when Create button clicked (same as nav3)
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'navigationItem',
      actionSubjectId: 'createButton',
      source: 'navigation',
    });
    setIsOpen(prev => !prev);
  }, [setIsOpen]);

  const renderMenu = useCallback(
    () => (
      <MenuGroup minWidth={149}>
        {globalBitbucketActions && globalBitbucketActions.length > 0 && (
          <Section>
            {globalBitbucketActions.map((menuItem: MenuItem) => (
              <Nav4LinkItem
                key={menuItem.url}
                menuItem={menuItem}
                onClick={onClose}
              />
            ))}
          </Section>
        )}
        {containerBitbucketActions && containerBitbucketActions.length > 0 && (
          <Section
            title={intl.formatMessage(messages.repositorySectionHeading)}
          >
            {containerBitbucketActions.map((menuItem: MenuItem) => (
              <Nav4LinkItem
                key={menuItem.url}
                menuItem={menuItem}
                onClick={onClose}
              />
            ))}
          </Section>
        )}
      </MenuGroup>
    ),
    [globalBitbucketActions, containerBitbucketActions, intl, onClose]
  );

  const renderTrigger = useCallback(
    (triggerProps: any) => {
      return (
        <CreateButton
          {...triggerProps}
          tooltip="Create"
          onClick={onToggle}
          testId="nav-v4-create-button"
          isSelected={isOpen}
        >
          Create
        </CreateButton>
      );
    },
    [onToggle, isOpen]
  );

  // Don't render if there are no actions
  if (!globalBitbucketActions?.length && !containerBitbucketActions?.length) {
    return null;
  }

  return (
    <Popup
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-start"
      trigger={renderTrigger}
      content={renderMenu}
      testId="top-nav-create-menu"
    />
  );
};
